import { z } from "zod";

/**
 * Sözleşme YÜKÜMLÜLÜKLERİ (CLOS-231 `[196]`).
 *
 * ### ⚠ Kaynak türü ayrımı kart kriteri
 *
 * *"Manuel oluşturulan kayıt ile AI tarafından yalnız önerilmiş kayıt açıkça ayrılır"* ve
 * *"AI önerisi kullanıcı tarafından doğrulanıp açıkça kaydedilmeden kesin operasyonel
 * yükümlülüğe dönüşmez."* `ai_suggested` bir TASLAKTIR; kabul edilince `ai_accepted`'e
 * geçer ve o andan sonra hatırlatma/bildirim üretimine girer.
 */
export const CONTRACT_OBLIGATION_SOURCE_TYPES = ["manual", "ai_suggested", "ai_accepted"] as const;
export type ContractObligationSourceType = (typeof CONTRACT_OBLIGATION_SOURCE_TYPES)[number];

export const CONTRACT_OBLIGATION_STATUSES = ["open", "completed", "cancelled"] as const;
export type ContractObligationStatus = (typeof CONTRACT_OBLIGATION_STATUSES)[number];

export const CONTRACT_OBLIGATION_REPEAT_FREQS = [
  "none",
  "daily",
  "weekly",
  "monthly",
  "yearly",
] as const;
export type ContractObligationRepeatFreq = (typeof CONTRACT_OBLIGATION_REPEAT_FREQS)[number];

export const CONTRACT_OBLIGATION_STATUS_NAMES: Record<ContractObligationStatus, string> = {
  open: "Açık",
  completed: "Tamamlandı",
  cancelled: "İptal edildi",
};

export const CONTRACT_OBLIGATION_SOURCE_NAMES: Record<ContractObligationSourceType, string> = {
  manual: "Manuel",
  ai_suggested: "AI önerisi (taslak)",
  ai_accepted: "AI önerisinden kabul edildi",
};

/**
 * Yükümlülük hatırlatma/görünüm KONFİGÜRASYONU.
 *
 * AÇIK-KARAR (CLOS-231): *"yükümlülük takip ekranındaki görünüm, hatırlatma eşikleri ve
 * durum seçenekleri konfigüre edilecektir."* Eşikler `contract_dates` emsaliyle aynı
 * desende: PLAN burada, DEFTER veritabanında (`lib/contractObligations/reminders.ts`).
 */
export const contractObligationConfigSchema = z.object({
  titleMaxLength: z.number().int().positive(),
  descriptionMaxLength: z.number().int().positive(),
  clauseRefMaxLength: z.number().int().positive(),
  reasonMaxLength: z.number().int().positive(),
  reminders: z.object({
    dayPrecisionTimezone: z.string().min(1),
    dayPrecisionLocalHour: z.number().int().min(0).max(23),
    thresholds: z.array(z.object({ key: z.string().min(1), daysBefore: z.number().int() })).min(1),
    backlogDays: z.number().int().positive(),
  }),
});
export type ContractObligationConfig = z.infer<typeof contractObligationConfigSchema>;

const ISO_GUN = /^\d{4}-\d{2}-\d{2}$/;
const uuidNullish = z.uuid().nullish();

/**
 * Oluşturma/güncelleme girdisi — İKİSİ AYNI ŞEKİL: kart *"sorumlu, taraf, son tarih,
 * tekrar kuralı ve durum doğrulanır"* diyor ve bu doğrulama create/update'te aynı.
 *
 * ⚠ `repeatInterval` yalnız `repeatFreq !== "none"` iken zorunlu — DB kısıtıyla AYNI kural,
 * burada erken ve TÜRKÇE mesajla reddediliyor (kısıt son çizgi, kullanıcı mesajı bu).
 */
export const contractObligationInputSchema = z
  .object({
    title: z.string().trim().min(1, "Başlık zorunludur").max(300, "Başlık çok uzun"),
    description: z.string().trim().max(4000, "Açıklama çok uzun").nullish(),
    partyId: uuidNullish,
    responsibleMembershipId: uuidNullish,
    sourceVersionId: uuidNullish,
    sourceClauseRef: z.string().trim().max(200, "Madde konumu çok uzun").nullish(),
    dueDate: z.string().regex(ISO_GUN, "Tarih YYYY-AA-GG biçiminde olmalıdır").nullish(),
    repeatFreq: z.enum(CONTRACT_OBLIGATION_REPEAT_FREQS).default("none"),
    repeatInterval: z.number().int().min(1).max(365).nullish(),
  })
  .superRefine((deger, ctx) => {
    if (deger.repeatFreq !== "none" && !deger.dueDate) {
      ctx.addIssue({
        code: "custom",
        path: ["dueDate"],
        message: "Tekrar kuralı için son tarih zorunludur",
      });
    }
    if (deger.repeatFreq === "none" && deger.repeatInterval != null) {
      ctx.addIssue({
        code: "custom",
        path: ["repeatInterval"],
        message: "Tekrarsız yükümlülükte tekrar aralığı verilemez",
      });
    }
    if (deger.repeatFreq !== "none" && deger.repeatInterval == null) {
      ctx.addIssue({
        code: "custom",
        path: ["repeatInterval"],
        message: "Tekrar kuralı için tekrar aralığı zorunludur",
      });
    }
  });
export type ContractObligationInput = z.infer<typeof contractObligationInputSchema>;

/** Güncelleme AYNI şekli taşır + eşzamanlılık kilidi. */
export const updateContractObligationSchema = contractObligationInputSchema.and(
  z.object({ revision: z.number().int().min(1, "Geçersiz sürüm") }),
);
export type UpdateContractObligationInput = z.infer<typeof updateContractObligationSchema>;

/**
 * ⚠ `acceptContractObligationSuggestionSchema` KALDIRILDI (CLOS-1117 · yetim uç I): kabul
 * ucu ve servisi gitti; `ai_accepted` kaynak türü veri olarak duruyor.
 */

export const contractObligationStatusInputSchema = z.object({
  to: z.enum(CONTRACT_OBLIGATION_STATUSES, "Geçerli bir durum seçilmelidir"),
  reason: z.string().trim().max(500, "Neden çok uzun").nullish(),
  revision: z.number().int().min(1, "Geçersiz sürüm"),
});
export type ContractObligationStatusInput = z.infer<typeof contractObligationStatusInputSchema>;

export const contractObligationParamsSchema = z.object({
  id: z.uuid(),
  obligationId: z.uuid(),
});

export type ContractObligationStatusHistoryEntry = {
  fromStatus: ContractObligationStatus | null;
  toStatus: ContractObligationStatus;
  reason: string | null;
  actorName: string | null;
  createdAt: string;
};

export type ContractObligation = {
  id: string;
  contractId: string;
  sourceType: ContractObligationSourceType;
  sourceVersionId: string | null;
  sourceClauseRef: string | null;
  title: string;
  description: string | null;
  partyId: string | null;
  partyName: string | null;
  responsibleMembershipId: string | null;
  responsibleName: string | null;
  dueDate: string | null;
  repeatFreq: ContractObligationRepeatFreq;
  repeatInterval: number | null;
  status: ContractObligationStatus;
  revision: number;
  createdAt: string;
  updatedAt: string;
  acceptedAt: string | null;
};

export type ContractObligationDetail = ContractObligation & {
  statusHistory: ContractObligationStatusHistoryEntry[];
};

export type ContractObligationList = {
  obligations: ContractObligation[];
};
